'use client'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Input } from '../ui/input'
import ActionButton from '../common/ActionButton'

type OtpInputBlockProps = {
  loading: boolean
  errorMsg?: string
  otpLength?: number
  onResendOTP: () => void
  onSubmitOTP: (otp: any) => void
  resetCount?: boolean
}

const OtpInputBlock = ({
  otpLength = 6,
  errorMsg,
  loading,
  onResendOTP,
  onSubmitOTP,
  resetCount,
}: OtpInputBlockProps) => {
  const otpWaitingTime = 30
  const [otpInput, setOtpInput] = useState(Array(otpLength).fill(''))
  const [error, setError] = useState<string | null>(null)
  const [count, setCount] = useState(otpWaitingTime)

  const inputRef = useRef<(any | null)[]>([])

  const timerRef = useRef<any | null>(null)

  useEffect(() => {
    if (otpInput.every((item) => item !== '')) {
      onSubmitOTP(otpInput)
    }
  }, [otpInput])

  useEffect(() => {
    if (resetCount) setCount(otpWaitingTime)
  }, [resetCount])

  useEffect(() => {
    if (count > 0) {
      timerRef.current = setInterval(() => {
        setCount((prevCount) => prevCount - 1)
      }, 1000)
    }
    return () => clearInterval(timerRef.current)
  }, [count])

  useEffect(() => {
    errorMsg && setError(errorMsg)
  }, [errorMsg])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setError(null)
    const value = e.target.value
    const newOtp = [...otpInput]
    newOtp[index] = value.substring(value.length - 1)
    setOtpInput(newOtp)

    if (value && index < otpInput.length - 1 && inputRef.current[index + 1]) {
      inputRef.current[index + 1].focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData('text')
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('')
      setOtpInput(newOtp)
      if (inputRef.current[5]) {
        inputRef.current[5].focus()
      }
    }
  }

  const handleKeyDown = (e: any, index: number) => {
    if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
      e.preventDefault()
    }
    if (
      e.key === 'Backspace' &&
      !otpInput[index] &&
      index > 0 &&
      inputRef.current[index - 1]
    ) {
      // Move focus to the previous input field on backspace
      inputRef.current[index - 1].focus()
    }
  }

  return (
    <div>
      <div className="flex gap-1 sm:gap-2 items-center justify-evenly w-full mb-2">
        {otpInput &&
          otpInput.length > 0 &&
          otpInput.map((key, index) => {
            return (
              <Fragment key={`input${index + 1}`}>
                <Input
                  ref={(input) => {
                    inputRef.current[index] = input
                  }}
                  type="number"
                  autoFocus={index === 0}
                  value={key}
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-md text-center px-0 bg-gray-100 border border-input shadow-sm text-body-md sm:text-body-lg ${
                    error ? 'ring-1 ring-danger-200' : ''
                  }`}
                  onChange={(e) => handleInputChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => handlePaste(e)}
                />
                {index === 2 && <span>-</span>}
              </Fragment>
            )
          })}
      </div>
      <p className="text-label-xs text-danger-500 mb-8 h-4">{error}</p>
      <div className="flex items-center justify-center gap-2 mb-4 ">
        <span className="text-label-sm text-gray-700">
          Didn’t receive the OTP?{' '}
        </span>
        {!!count && (
          <span className="text-label-sm text-gray-700">Retry in</span>
        )}
        {!!count && (
          <span className="text-primary text-label-sm font-semibold">
            00:{count < 10 ? `0${count}` : `${count}`}
          </span>
        )}
        {!count && (
          <button
            className="text-primary text-label-sm font-semibold"
            onClick={() => {
              setOtpInput(Array(otpLength).fill(''))
              onResendOTP()
            }}
          >
            Resend
          </button>
        )}
      </div>
      <ActionButton
        variant="primary"
        title="Verify and Proceed"
        loading={loading}
        disabled={otpInput.some((item) => item === '') || loading}
        onClick={() => onSubmitOTP(otpInput)}
        className="mb-4 text-label-lg font-semibold py-3 w-full"
      />
    </div>
  )
}

export default OtpInputBlock
